import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import useAuthStore from '../../store/authStore';
import { initializeSubscription, confirmSubscription } from '../../services/subscriptionService';
import { configService } from '../../services/configService';

const MONTHLY_PLAN = {
  name: 'Monthly Access',
  price: Number(import.meta.env.VITE_MONTHLY_SUBSCRIPTION_PRICE || 3500),
  features: [
    'Access e-commerce flat Lagos rates',
    'Base prices for all shipments',
    'Auto-renew monthly',
    'Priority services',
    'COD available',
    'Dedicated account officer',
    "Discount on international cargo shipments",
    'Return package management',
  ]
};

function normalizePlanKey(name) {
  return 'monthly';
}

const PlanSelection = () => {
  const navigate = useNavigate();
  const { currentUser, isAuthenticated } = useAuth();
  const { updateUserPlan } = useAuthStore();
  const [plan, setPlan] = useState(MONTHLY_PLAN);
  const [loading, setLoading] = useState(true);
  const [prefilled, setPrefilled] = useState(false);
  const [error, setError] = useState(null);
  const [isSubscribing, setIsSubscribing] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await configService.getPricingConfig();
        const cfg = resp?.config || {};
        if (typeof cfg.monthlySubscriptionPrice === 'number' && cfg.monthlySubscriptionPrice > 0) {
          setPlan(prev => ({ ...prev, price: Number(cfg.monthlySubscriptionPrice) }));
        }
      } catch { }
      setPrefilled(true);
      setLoading(false);
    })();
  }, []);

  function getCurrentUserEmail() {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return u.email || '';
    } catch {
      return '';
    }
  }


  const handleSubscribe = async () => {
    try {
      setIsSubscribing(true);
      setError(null);
      if (!isAuthenticated) {
        setError('Please sign in to subscribe');
        navigate('/auth');
        return;
      }
      const email = getCurrentUserEmail();
      const res = await initializeSubscription(plan.name, email);
      if (res && res.authorization_url) {
        window.location.href = res.authorization_url;
        return;
      }
      setError('Failed to create checkout session. Please try again or contact support.');
    } catch (e) {
      console.error('Subscription initialization error:', e);
      setError(e.message || 'Failed to connect to payment gateway. Please check your connection and try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Choose Your Plan</h1>
            <p className="text-gray-600 mt-2">Select the perfect plan for your business</p>
          </div>
          <button onClick={() => navigate('/dashboard/user')} className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50">
            ← Back
          </button>
        </div>

        {prefilled && (
          <div className="mb-4 text-blue-700 bg-blue-50 border border-blue-200 p-3 rounded">
            Subscribe monthly to unlock flat e-commerce Lagos rates and base shipment pricing.
          </div>
        )}

        {error && (
          <div className="mb-4 text-red-700 bg-red-50 border border-red-200 p-3 rounded">
            {error}
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-start-2 bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all transform hover:-translate-y-1">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
              <div className="mt-4">
                <span className="text-4xl font-bold text-red-600">₦{Number(plan.price).toLocaleString()}</span>
                <span className="text-gray-500">/month</span>
              </div>
            </div>
            <ul className="space-y-3 mb-8">
              {(plan.features || []).map((feature, i) => (
                <li key={i} className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700 text-sm">{feature}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={handleSubscribe}
              disabled={isSubscribing}
              className="w-full py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transform hover:-translate-y-0.5 transition-all disabled:opacity-50"
            >
              {isSubscribing ? 'Processing...' : 'Subscribe Monthly'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanSelection;
